import { z } from "zod";
import { ConfigurationError, appEnvironmentSchema, selectEnvironment } from "./shared";

const clientKeys = [
  "VITE_APP_ENV",
  "VITE_API_BASE_URL",
  "VITE_DATA_PROVIDER",
  "VITE_SUPABASE_URL",
  "VITE_SUPABASE_PUBLISHABLE_KEY",
] as const;
const secretNamePattern = /(?:SECRET|TOKEN|PASSWORD|PRIVATE_KEY|SERVICE_ROLE|DATABASE_URL)/i;

export const clientEnvironmentSchema = z
  .object({
    VITE_APP_ENV: appEnvironmentSchema,
    VITE_API_BASE_URL: z.string().url(),
    VITE_DATA_PROVIDER: z.enum(["fixture", "api"]),
    VITE_SUPABASE_URL: z.string().url(),
    VITE_SUPABASE_PUBLISHABLE_KEY: z
      .string()
      .regex(/^sb_publishable_[A-Za-z0-9_-]+$/, "Must be a Supabase publishable key"),
  })
  .strict();

export type ClientEnvironment = Readonly<z.infer<typeof clientEnvironmentSchema>>;

export function parseClientEnvironment(input: Record<string, unknown>): ClientEnvironment {
  const exposedSecretNames = Object.keys(input).filter(
    (key) => secretNamePattern.test(key) && input[key] !== undefined,
  );

  if (exposedSecretNames.length > 0) {
    const syntheticError = new z.ZodError(
      exposedSecretNames.map((key) => ({
        code: z.ZodIssueCode.custom,
        path: [key],
        message: "Secret-like variables are prohibited in client configuration",
      })),
    );
    throw new ConfigurationError("client", syntheticError);
  }

  const result = clientEnvironmentSchema.safeParse(selectEnvironment(input, clientKeys));
  if (!result.success) throw new ConfigurationError("client", result.error);
  return Object.freeze(result.data);
}
